import { DateObject } from 'react-multi-date-picker'
import {
  SPECIAL_PRICE_FILTER_VARIANTS,
  SPECIAL_PRICE_ORDERED_FILTER_CONFIG
} from './specialPriceFilterConstants'

const SPECIAL_PRICE_ATTRIBUTE_LABEL_PREFIX = /^Atributo\s+(\d+)\s*:/i
const SPECIAL_PRICE_CLIENT_GROUP_KEY = 'CLIENTE'
const SPECIAL_PRICE_CLIENT_GROUP_LABEL = 'Cliente individual'
const SPECIAL_PRICE_DATE_RANGE_LABEL_FORMAT = 'DD MMM'

const getAttributeGroupLabel = (label = '') => {
  const match = label.match(SPECIAL_PRICE_ATTRIBUTE_LABEL_PREFIX)
  if (!match) return null

  const index = Number(match[1])
  if (!Number.isFinite(index) || index <= 0) return 'ATRIBUTO'

  return `ATRIBUTO ${String.fromCharCode(64 + index)}`
}

export const buildClientAttributeGroups = (options = []) => {
  const groupedAttributes = new Map()
  const clientOptions = []

  options.forEach((option) => {
    if (!option?.value || !option?.label) return

    const explicitGroupLabel = option.groupLabel || option.group_label
    const explicitGroupKey = option.group || option.groupKey || option.group_key

    if (explicitGroupLabel) {
      const normalizedGroupKey = explicitGroupKey || explicitGroupLabel

      if (!groupedAttributes.has(normalizedGroupKey)) {
        groupedAttributes.set(normalizedGroupKey, {
          label: explicitGroupLabel,
          options: []
        })
      }

      groupedAttributes.get(normalizedGroupKey).options.push(option)
      return
    }

    const attributeGroupLabel = getAttributeGroupLabel(option.label)
    if (!attributeGroupLabel) {
      clientOptions.push(option)
      return
    }

    if (!groupedAttributes.has(attributeGroupLabel)) {
      groupedAttributes.set(attributeGroupLabel, {
        label: attributeGroupLabel,
        options: []
      })
    }

    groupedAttributes.get(attributeGroupLabel).options.push(option)
  })

  const attributeGroups = Array.from(groupedAttributes.entries())
    .sort(([left], [right]) => left.localeCompare(right, 'es', { sensitivity: 'base' }))
    .map(([groupKey, groupData]) => ({
      key: groupKey,
      label: groupData.label,
      options: groupData.options
    }))

  return [
    ...attributeGroups,
    ...(clientOptions.length
      ? [
          {
            key: SPECIAL_PRICE_CLIENT_GROUP_KEY,
            label: SPECIAL_PRICE_CLIENT_GROUP_LABEL,
            options: clientOptions
          }
        ]
      : [])
  ]
}

export const normalizeDateRangeValue = (dateRange) => {
  if (!Array.isArray(dateRange) || dateRange.length === 0) return null

  return dateRange
    .map((value) => {
      if (!value) return null

      if (value instanceof DateObject) return value

      if (value?.format && typeof value?.toDate === 'function') {
        return new DateObject(value.toDate())
      }

      if (typeof value === 'string') {
        return new DateObject({ date: value, format: 'YYYY-MM-DD' })
      }

      return new DateObject(value)
    })
    .filter(Boolean)
}

export const hasSelectedDateRange = (dateRange) =>
  Array.isArray(dateRange) && dateRange.length > 0

export const hasCompleteDateRange = (dateRange) =>
  Array.isArray(dateRange) && dateRange.length === 2

export const getNormalizedDateRangeChange = (dateRange) => {
  const normalizedDateRange = normalizeDateRangeValue(dateRange)

  return hasSelectedDateRange(normalizedDateRange) ? normalizedDateRange : null
}

export const formatDateRangeLabel = (dateRange) => {
  if (!hasSelectedDateRange(dateRange)) return ''

  return dateRange
    .map((value) => (value?.format ? value.format(SPECIAL_PRICE_DATE_RANGE_LABEL_FORMAT) : null))
    .filter(Boolean)
    .join(' - ')
}

export const getDateRangeFieldClassName = (dateRange) =>
  `special-price-filters__field special-price-filters__field--date-range${
    hasCompleteDateRange(dateRange) ? ' special-price-filters__field--date-range--expanded' : ''
  }`

export const buildSpecialPriceFilterItems = ({
  clientAttributeFilter,
  productFilter,
  salesUnitFilter,
  statusFilter,
  clientAttributeGroups,
  productOptions,
  salesUnitOptions,
  statusOptions,
  onClientAttributeChange,
  onProductChange,
  onSalesUnitChange,
  onStatusChange
}) => {
  const filterValueMap = {
    client_attribute: clientAttributeFilter,
    product: productFilter,
    sales_unit: salesUnitFilter,
    status: statusFilter
  }

  const filterOptionsMap = {
    client_attribute: clientAttributeGroups,
    product: productOptions,
    sales_unit: salesUnitOptions,
    status: statusOptions
  }

  const filterChangeMap = {
    client_attribute: onClientAttributeChange,
    product: onProductChange,
    sales_unit: onSalesUnitChange,
    status: onStatusChange
  }

  return SPECIAL_PRICE_ORDERED_FILTER_CONFIG.map((filterConfig) => ({
    ...filterConfig,
    options: filterOptionsMap[filterConfig.key] || [],
    value: filterValueMap[filterConfig.key],
    onChange: filterChangeMap[filterConfig.key],
    isGrouped: filterConfig.variant === SPECIAL_PRICE_FILTER_VARIANTS.GROUPED
  }))
}
